import type { QueryClient } from '@tanstack/react-query';
import type { components } from '../api';
import { applyCacheAction, clearProductListWindow } from './cache-actions';

export type LiveEvent = components['schemas']['LiveEvent'];

type ConnectOptions = {
  url: string;
  pollingOnly: boolean;
  queryClient: QueryClient;
  // Every event but the heartbeat, after its cache action.
  onEvent: (event: LiveEvent) => void;
  onPolling: (polling: boolean) => void;
};

const SECOND = 1_000;
const MINUTE = 60 * SECOND;
// Live updates §2: ready carries the retry hint (3 s) and the heartbeat interval (15 s until then).
const RETRY_HINT_MS = 3 * SECOND;
const HEARTBEAT_MS = 15 * SECOND;
// UI architecture §5.
const MISSED_HEARTBEATS = 3;
const HIDDEN_CLOSE_MS = 5 * MINUTE;
const POLL_MS = 30 * SECOND;
const STREAM_RETRY_MS = 5 * MINUTE;

// The named events of live updates §3 but the heartbeat; an unknown type has no listener, so it is
// ignored.
const eventTypes = [
  'ready',
  'resync',
  'message.created',
  'conversation.updated',
  'handoff.created',
  'draft.created',
  'notification.created',
  'sync.progress',
  'connection.changed',
  'domain.event',
] satisfies LiveEvent['type'][];

/**
 * Opens the app session's stream and keeps it alive (UI architecture §5): the heartbeat watchdog,
 * the reopen after a failure, the hidden-tab close and the polling fallback. `pollingOnly` never
 * opens a stream and polls the queries with meta.poll true. Returns the function that closes the
 * stream and clears every timer.
 */
export function connect(options: ConnectOptions): () => void {
  const { url, pollingOnly, queryClient, onEvent, onPolling } = options;
  let source: EventSource | null = null;
  let sourcesOpened = 0;
  let readySeen = false;
  let failures = 0;
  let heartbeatMs = HEARTBEAT_MS;
  let polling = false;
  let watchdog: ReturnType<typeof setTimeout> | undefined;
  let reopenTimer: ReturnType<typeof setTimeout> | undefined;
  let retryTimer: ReturnType<typeof setTimeout> | undefined;
  let hiddenTimer: ReturnType<typeof setTimeout> | undefined;
  let pollTimer: ReturnType<typeof setInterval> | undefined;

  // Polling-only mode (the admin panel) refetches only the queries that ask for it with meta.poll
  // true: the other screens have their own cadence or none, and audited reads never poll (admin
  // panel screens §1 rule 5). Otherwise every active query refetches.
  const refetchActive = () =>
    void queryClient.refetchQueries({
      type: 'active',
      predicate: (query) => !pollingOnly || query.meta?.poll === true,
    });

  // A half-open connection fires no error: three heartbeat intervals without any event reopen it.
  // While polling, a retry that hangs counts as a failed one: the next try comes 5 minutes later.
  function stamp(): void {
    clearTimeout(watchdog);
    watchdog = setTimeout(() => {
      if (polling) return closeAndRetry();
      closeSource();
      openSource();
    }, MISSED_HEARTBEATS * heartbeatMs);
  }

  function closeSource(): void {
    source?.close();
    source = null;
    clearTimeout(watchdog);
  }

  function openSource(): void {
    sourcesOpened += 1;
    const opened = new EventSource(url, { withCredentials: true });
    source = opened;
    opened.addEventListener('open', () => {
      failures = 0;
      if (polling) stopPolling();
    });
    opened.addEventListener('error', () => {
      failures += 1;
      // The browser reconnects this same EventSource and resends Last-Event-ID.
      if (!polling && opened.readyState === EventSource.CONNECTING) return;
      closeAndRetry();
    });
    // The named heartbeat only feeds the watchdog (live updates §2).
    opened.addEventListener('heartbeat', stamp);
    for (const type of eventTypes) opened.addEventListener(type, receive);
    stamp();
  }

  // Closes a failed stream and plans the next try; retries wait for the tab to be visible again.
  function closeAndRetry(): void {
    closeSource();
    if (document.visibilityState === 'hidden') return;
    if (polling) retryTimer = setTimeout(openSource, STREAM_RETRY_MS);
    else if (failures >= 2) startPolling();
    else reopenTimer = setTimeout(openSource, RETRY_HINT_MS);
  }

  function receive(message: MessageEvent<string>): void {
    const event = JSON.parse(message.data) as LiveEvent;
    if (event.type === 'ready') {
      heartbeatMs = event.data.heartbeat_s * SECOND;
      // Only the first ready of the session's first stream comes before anything could be missed.
      // Any later one that did not resume came without Last-Event-ID (a stream this provider
      // reopened, or the browser's reconnect of a stream that had no data event yet): what it
      // missed is refetched.
      const first = !readySeen && sourcesOpened === 1;
      readySeen = true;
      if (!event.data.resumed && !first) void queryClient.invalidateQueries();
    } else if (event.type === 'resync') {
      void queryClient.invalidateQueries();
    } else {
      applyCacheAction(queryClient, event);
    }
    stamp();
    onEvent(event);
  }

  function startPolling(): void {
    polling = true;
    onPolling(true);
    pollTimer = setInterval(refetchActive, POLL_MS);
    if (!pollingOnly) retryTimer = setTimeout(openSource, STREAM_RETRY_MS);
  }

  function stopPolling(): void {
    polling = false;
    onPolling(false);
    clearInterval(pollTimer);
  }

  // Hidden: the stream closes after 5 minutes; polling and retries pause. Visible: every active
  // query refetches, whether or not the stream had closed, and a closed stream reopens.
  function onVisibilityChange(): void {
    if (document.visibilityState === 'hidden') {
      hiddenTimer = setTimeout(closeSource, HIDDEN_CLOSE_MS);
      clearTimeout(reopenTimer);
      clearTimeout(retryTimer);
      clearInterval(pollTimer);
      return;
    }
    clearTimeout(hiddenTimer);
    refetchActive();
    if (polling) pollTimer = setInterval(refetchActive, POLL_MS);
    if (source === null && !pollingOnly) openSource();
  }

  document.addEventListener('visibilitychange', onVisibilityChange);
  if (pollingOnly) startPolling();
  else openSource();
  if (document.visibilityState === 'hidden') onVisibilityChange();

  return () => {
    document.removeEventListener('visibilitychange', onVisibilityChange);
    closeSource();
    clearTimeout(reopenTimer);
    clearTimeout(retryTimer);
    clearTimeout(hiddenTimer);
    clearInterval(pollTimer);
    clearProductListWindow(queryClient);
  };
}
